/**
 * Pure presentation component for the extraction data section.
 *
 * Displays extracted contract data organized by categories (top-level
 * keys of the `extractedData` JSON object). Each category renders its
 * line items, a warning badge in the header when any items have
 * `isConflict === true`, a per-category "Mark Conflicts Resolved"
 * button when unresolved conflicts exist, and an inline form for
 * adding new items as a single free-text field (the canonical
 * `ExtractedTerm` shape only carries `text`).
 *
 * Each line item supports inline editing: hover (web) or always
 * (native) shows pencil + trash buttons; clicking the text or pencil
 * opens a per-row inline `Input` with checkmark (save) and X (cancel)
 * affordances. The trash button opens an `AlertDialog` confirmation
 * with the exact copy required by SE-4384's AC. Items that were
 * recently edited or added carry a blue highlight (`bg-surface-info-subtle`,
 * which auto-inverts to a dark shade in dark mode via the GlueStack
 * CSS-variable palette — no `dark:` override needed; see SE-4599)
 * until the next refetch lands.
 *
 * Conflicting items render with an amber background and warning icon
 * per Story 2.3 (SE-4342). Conflict state is read from the live
 * extraction data — the View does not own it. The Container pairs the
 * "Mark Conflicts Resolved" click with a server mutation
 * (`resolveCategoryConflicts`) and a query refetch; by the time this
 * component re-renders the `isConflict` flags reflect the server state.
 * Resolve / edit / delete failures surface inline as dismissable error
 * messages.
 *
 * When `readOnly` is `true` (extraction is in `SAVED` status per
 * SE-4386), all editing affordances are suppressed: no "Add line item"
 * button, no inline add form, no per-category "Mark Conflicts
 * Resolved" button, and each row renders without pencil / trash
 * affordances or the inline-edit input. Conflict + recently-modified
 * presentation still renders so the data remains legible.
 * @module features/contracts/components/ExtractionDataSection/ExtractionDataSectionView
 */

import { AlertTriangle, Plus } from "lucide-react-native";
import React, { memo } from "react";
import { Platform } from "react-native";

import type {
  DeletingItemState,
  EditingItemState,
  ItemErrorState,
} from "./ExtractionDataSectionContainer";
import LineItemRow from "@/features/contracts/components/organisms/LineItemRow";
import type { ExtractionLineItem } from "@/features/contracts/types";

import {
  AlertDialog,
  AlertDialogBackdrop,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
} from "@/components/atoms/AlertDialog";
import { Button } from "@/components/atoms/Button";
import { Box } from "@/components/atoms/Box";
import { Heading } from "@/components/atoms/Heading";
import { Icon } from "@/components/atoms/Icon";
import { Stack } from "@/components/atoms/Stack";
import { Input } from "@/components/atoms/Input";
import { Text } from "@/components/atoms/Text";

// SE-4333 item E: use the shared utility instead of an ad-hoc inline
// formatter that only handled snake_case. Handles camelCase +
// snake_case + SCREAMING_CASE uniformly and title-cases the output so
// every surface renders "Fixed Terms" regardless of backend casing.
import { formatCategoryDisplayName as formatCategoryName } from "@/features/contracts/utils/formatCategoryDisplayName";

/** Props for {@link ExtractionDataSectionView}. */
interface Props {
  /** Categories parsed from the extraction data, with derived conflict counts. */
  readonly visibleCategories: readonly {
    readonly name: string;
    readonly items: readonly ExtractionLineItem[];
    readonly unresolvedConflictCount: number;
  }[];
  /** Category whose add-line-item form is currently expanded, or null. */
  readonly expandedCategory: string | null;
  /** Current text value in the add form. */
  readonly newItemText: string;
  /** Whether an add-line-item request is in flight. */
  readonly isAdding: boolean;
  /** Category whose resolve-conflicts mutation is currently in flight, or null. */
  readonly resolvingCategory: string | null;
  /** Whether any resolve-conflicts mutation is in flight (guards all buttons). */
  readonly isResolving: boolean;
  /**
   * Per-category error messages from the last failed resolve attempt.
   * Entries are cleared on the next successful resolve or when the
   * user expands the add-line-item form for that category.
   */
  readonly resolveErrors: Readonly<Record<string, string>>;
  /** Pre-bound expand handlers keyed by category name. */
  readonly expandHandlers: Readonly<Record<string, () => void>>;
  /** Pre-bound resolve-conflicts handlers keyed by category name. */
  readonly resolveConflictHandlers: Readonly<
    Record<string, () => Promise<void>>
  >;
  /** The line item currently being edited inline, or null when none. */
  readonly editingItem: EditingItemState | null;
  /** Whether the edit mutation is currently in flight. */
  readonly isEditingMutation: boolean;
  /** Per-item error message from the last failed edit, or null. */
  readonly editError: ItemErrorState | null;
  /** The line item awaiting delete confirmation, or null when the dialog is closed. */
  readonly deletingItem: DeletingItemState | null;
  /** Whether the delete mutation is currently in flight. */
  readonly isDeletingMutation: boolean;
  /** Per-item error message from the last failed delete, or null. */
  readonly deleteError: ItemErrorState | null;
  /** Set of `${category}:${index}` keys that should render the blue highlight. */
  readonly recentlyModified: ReadonlySet<string>;
  /** Invoked when the user cancels the inline add form. */
  readonly onCollapseCategory: () => void;
  /** Invoked when the new item text input changes. */
  readonly onNewItemTextChange: (text: string) => void;
  /** Invoked when the user submits the add form. */
  readonly onSubmitLineItem: () => void;
  /** Invoked when the user clicks an item's text or pencil to begin editing. */
  readonly onStartEdit: (
    category: string,
    index: number,
    currentText: string
  ) => void;
  /** Invoked when the inline edit input value changes. */
  readonly onChangeEditDraft: (text: string) => void;
  /** Invoked when the user confirms the edit (checkmark). */
  readonly onSaveEdit: () => void;
  /** Invoked when the user cancels the edit (X). */
  readonly onCancelEdit: () => void;
  /** Invoked when the user clicks an item's trash icon to request deletion. */
  readonly onRequestDelete: (category: string, index: number) => void;
  /** Invoked when the user confirms the delete from the AlertDialog. */
  readonly onConfirmDelete: () => void;
  /** Invoked when the user cancels the delete from the AlertDialog. */
  readonly onCancelDelete: () => void;
  /**
   * When `true`, suppresses all editing affordances: the "Add line
   * item" button, the inline add form, the per-category
   * "Mark Conflicts Resolved" button, and each row's pencil / trash
   * affordances. Conflict indicators themselves still render (driven
   * by each item's `isConflict` flag) — a saved extraction should have
   * no remaining conflicts, but if the flag somehow persists we still
   * surface it informationally.
   */
  readonly readOnly?: boolean;
  /**
   * When provided, only the category matching this key is rendered.
   * Allows a wrapper (e.g. `ContractExtractionView`) to mount one
   * `ExtractionDataSection` per category inside its own collapsible
   * header, so the section renders the items but the wrapper owns the
   * heading / collapse state. When omitted (default), every category
   * renders in its original order — behavior is unchanged for legacy
   * callers (Phase 4 Financials tab today).
   */
  readonly categoryFilter?: string;
}

/**
 * Renders a single `LineItemRow` for a category. Hoisted out of the
 * View body so the per-item conditional derivations (isBeingEdited,
 * editErrorMessage, deleteErrorMessage) don't count against the
 * outer category-map callback's cognitive complexity budget.
 * @param args - Render arguments for the line item row
 * @param args.item - The line item to render
 * @param args.index - Position of the item within its category
 * @param args.categoryName - The owning category's name (used for keys + matching)
 * @param args.editingItem - Currently-edited item state (or null)
 * @param args.recentlyModified - Set of "category:index" keys to highlight
 * @param args.editError - Last edit error (per-item) or null
 * @param args.deleteError - Last delete error (per-item) or null
 * @param args.isEditingMutation - Whether the edit mutation is in flight
 * @param args.onStartEdit - Begins inline edit for an item
 * @param args.onChangeEditDraft - Inline-edit input change handler
 * @param args.onSaveEdit - Confirms inline edit
 * @param args.onCancelEdit - Cancels inline edit
 * @param args.onRequestDelete - Opens delete confirmation for an item
 * @param args.readOnly - Suppresses editing affordances when true
 * @param args.itemText - The current text of the item
 * @returns The rendered LineItemRow
 */
const renderLineItem = (args: {
  item: ExtractionLineItem;
  index: number;
  categoryName: string;
  editingItem: EditingItemState | null;
  recentlyModified: ReadonlySet<string>;
  editError: ItemErrorState | null;
  deleteError: ItemErrorState | null;
  isEditingMutation: boolean;
  onStartEdit: (category: string, index: number, currentText: string) => void;
  onChangeEditDraft: (text: string) => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onRequestDelete: (category: string, index: number) => void;
  readOnly: boolean;
  itemText: string;
}) => {
  const {
    item,
    index,
    categoryName,
    editingItem,
    recentlyModified,
    editError,
    deleteError,
    isEditingMutation,
    onStartEdit,
    onChangeEditDraft,
    onSaveEdit,
    onCancelEdit,
    onRequestDelete,
    readOnly,
    itemText,
  } = args;
  const isBeingEdited =
    editingItem !== null &&
    editingItem.category === categoryName &&
    editingItem.index === index;
  const isRecentlyModified = recentlyModified.has(`${categoryName}:${index}`);
  const editErrorMessage =
    editError !== null &&
    editError.category === categoryName &&
    editError.index === index
      ? editError.message
      : null;
  const deleteErrorMessage =
    deleteError !== null &&
    deleteError.category === categoryName &&
    deleteError.index === index
      ? deleteError.message
      : null;

  return (
    <LineItemRow
      key={`${categoryName}-${index}`}
      category={categoryName}
      index={index}
      text={itemText}
      isConflict={item.isConflict}
      isRecentlyModified={isRecentlyModified}
      isBeingEdited={isBeingEdited}
      draftText={isBeingEdited ? editingItem.draftText : ""}
      isEditingMutation={isEditingMutation}
      editErrorMessage={editErrorMessage}
      deleteErrorMessage={deleteErrorMessage}
      onStartEdit={onStartEdit}
      onChangeEditDraft={onChangeEditDraft}
      onSaveEdit={onSaveEdit}
      onCancelEdit={onCancelEdit}
      onRequestDelete={onRequestDelete}
      readOnly={readOnly}
    />
  );
};

/**
 * Renders the per-category "Mark Conflicts Resolved" section
 * (button + inline error message). Hoisted out of the category-map
 * callback so its nested ternaries (spinner-vs-icon, label, error
 * presence) don't count toward the View's cognitive complexity budget.
 * @param args - Render arguments for the resolve-conflicts section
 * @param args.categoryName - The owning category name (used in testIDs)
 * @param args.visible - Whether the section should render at all
 * @param args.isThisCategoryResolving - Whether THIS category's mutation is in flight
 * @param args.isResolving - Whether ANY resolve mutation is in flight (disables button)
 * @param args.categoryResolveError - Inline error message for the last failure (or undefined)
 * @param args.onPress - Click handler bound to this category's resolver
 * @returns The rendered section, or null when `visible` is false
 */
const renderResolveConflictsSection = (args: {
  categoryName: string;
  visible: boolean;
  isThisCategoryResolving: boolean;
  isResolving: boolean;
  categoryResolveError: string | undefined;
  onPress: () => Promise<void>;
}) => {
  const {
    categoryName,
    visible,
    isThisCategoryResolving,
    isResolving,
    categoryResolveError,
    onPress,
  } = args;
  if (!visible) return null;
  return (
    <Stack className="gap-1">
      <Button
        testID={`mark-conflicts-resolved-${categoryName}`}
        label={
          isThisCategoryResolving ? "Resolving..." : "Mark Conflicts Resolved"
        }
        size="sm"
        variant="outline"
        tone="secondary"
        icon={AlertTriangle}
        onPress={onPress}
        isDisabled={isResolving}
        isLoading={isThisCategoryResolving}
        className="self-start"
      />
      {categoryResolveError !== undefined ? (
        <Box testID={`resolve-error-${categoryName}`}>
          <Text variant="caption" className="text-needs-semantic-error-600">
            {categoryResolveError}
          </Text>
        </Box>
      ) : null}
    </Stack>
  );
};

/**
 * Renders the extraction data organized by categories with inline add
 * forms, inline edit affordances, delete confirmation, and conflict
 * styling + resolution controls.
 * @param props - View props
 * @param props.visibleCategories - Categories parsed from the extraction data, with derived conflict counts.
 * @param props.expandedCategory - Category whose add-line-item form is currently expanded, or null.
 * @param props.newItemText - Current text value in the add form.
 * @param props.isAdding - Whether an add-line-item request is in flight.
 * @param props.resolvingCategory - Category whose resolve-conflicts mutation is currently in flight, or null.
 * @param props.isResolving - Whether any resolve-conflicts mutation is in flight (guards all buttons).
 * @param props.resolveErrors - Per-category error messages from the last failed resolve attempt. Entries are cleared on the next successful resolve or when the user expands the add-line-item form for that category.
 * @param props.expandHandlers - Pre-bound expand handlers keyed by category name.
 * @param props.resolveConflictHandlers - Pre-bound resolve-conflicts handlers keyed by category name.
 * @param props.editingItem - The line item currently being edited inline, or null when none.
 * @param props.isEditingMutation - Whether the edit mutation is currently in flight.
 * @param props.editError - Per-item error message from the last failed edit, or null.
 * @param props.deletingItem - The line item awaiting delete confirmation, or null when the dialog is closed.
 * @param props.isDeletingMutation - Whether the delete mutation is currently in flight.
 * @param props.deleteError - Per-item error message from the last failed delete, or null.
 * @param props.recentlyModified - Set of `${category}:${index}` keys that should render the blue highlight.
 * @param props.onCollapseCategory - Invoked when the user cancels the inline add form.
 * @param props.onNewItemTextChange - Invoked when the new item text input changes.
 * @param props.onSubmitLineItem - Invoked when the user submits the add form.
 * @param props.onStartEdit - Invoked when the user clicks an item's text or pencil to begin editing.
 * @param props.onChangeEditDraft - Invoked when the inline edit input value changes.
 * @param props.onSaveEdit - Invoked when the user confirms the edit (checkmark).
 * @param props.onCancelEdit - Invoked when the user cancels the edit (X).
 * @param props.onRequestDelete - Invoked when the user clicks an item's trash icon to request deletion.
 * @param props.onConfirmDelete - Invoked when the user confirms the delete from the AlertDialog.
 * @param props.onCancelDelete - Invoked when the user cancels the delete from the AlertDialog.
 * @param props.readOnly - When `true`, suppresses all editing affordances: the "Add line item" button, the inline add form, the per-category "Mark Conflicts Resolved" button, and each row's pencil / trash affordances. Conflict indicators themselves still render (driven by each item's `isConflict` flag) — a saved extraction should have no remaining conflicts, but if the flag somehow persists we still surface it informationally.
 * @param props.categoryFilter - When provided, only the category matching this key is rendered. Allows a wrapper (e.g. `ContractExtractionView`) to mount one `ExtractionDataSection` per category inside its own collapsible header, so the section renders the items but the wrapper owns the heading / collapse state. When omitted (default), every category renders in its original order — behavior is unchanged for legacy callers (Phase 4 Financials tab today).
 * @returns The rendered extraction data section
 */
const ExtractionDataSectionView = ({
  visibleCategories,
  expandedCategory,
  newItemText,
  isAdding,
  resolvingCategory,
  isResolving,
  resolveErrors,
  expandHandlers,
  resolveConflictHandlers,
  editingItem,
  isEditingMutation,
  editError,
  deletingItem,
  isDeletingMutation,
  deleteError,
  recentlyModified,
  onCollapseCategory,
  onNewItemTextChange,
  onSubmitLineItem,
  onStartEdit,
  onChangeEditDraft,
  onSaveEdit,
  onCancelEdit,
  onRequestDelete,
  onConfirmDelete,
  onCancelDelete,
  readOnly = false,
  categoryFilter,
}: Props) => (
  <>
    <Stack className="gap-4">
      {visibleCategories.map(category => {
        const categoryHasConflicts = category.unresolvedConflictCount > 0;
        const isThisCategoryResolving =
          resolvingCategory === category.name && isResolving;
        const categoryResolveError = resolveErrors[category.name];
        return (
          <Stack key={category.name} className="gap-2">
            {/*
             * When a `categoryFilter` is applied, the parent is rendering
             * its own category header (e.g. `CollapsibleTierHeader` inside
             * `ContractExtractionView`) so the inline heading text becomes
             * a duplicate. Hide JUST the heading `Text` in that mode — the
             * conflict badge continues to render so resolvers can see
             * warnings even when wrapped by an outer header.
             */}
            <Stack direction="horizontal" className="items-center gap-2">
              {categoryFilter === undefined ? (
                <Text variant="title-sm" className="text-content-primary">
                  {formatCategoryName(category.name)}
                </Text>
              ) : null}
              {categoryHasConflicts ? (
                <Box
                  testID={`category-conflict-badge-${category.name}`}
                  accessibilityLabel={`${category.unresolvedConflictCount} unresolved conflicts`}
                >
                  <Stack
                    direction="horizontal"
                    className="items-center gap-1 rounded-none bg-status-warning-surface px-2 py-micro"
                  >
                    <Icon
                      as={AlertTriangle}
                      size="2xs"
                      className="text-status-warning-strong"
                    />
                    <Text
                      variant="unstyled"
                      className="font-medium text-micro uppercase text-status-warning-strong"
                    >
                      {category.unresolvedConflictCount}{" "}
                      {category.unresolvedConflictCount === 1
                        ? "conflict"
                        : "conflicts"}
                    </Text>
                  </Stack>
                </Box>
              ) : null}
            </Stack>

            {category.items.map((item, index) =>
              renderLineItem({
                item,
                index,
                categoryName: category.name,
                editingItem,
                recentlyModified,
                editError,
                deleteError,
                isEditingMutation,
                onStartEdit,
                onChangeEditDraft,
                onSaveEdit,
                onCancelEdit,
                onRequestDelete,
                readOnly,
                itemText: item.text,
              })
            )}

            {renderResolveConflictsSection({
              categoryName: category.name,
              visible: categoryHasConflicts && !readOnly,
              isThisCategoryResolving,
              isResolving,
              categoryResolveError,
              onPress: resolveConflictHandlers[category.name],
            })}

            {readOnly ? null : expandedCategory === category.name ? (
              <Box
                testID="line-item-form"
                className="rounded-none border border-surface-muted p-3"
              >
                <Stack className="gap-2">
                  <Input
                    size="sm"
                    variant="outline"
                    field={{
                      placeholder: "Describe the new line item",
                      value: newItemText,
                      onChangeText: onNewItemTextChange,
                      editable: !isAdding,
                    }}
                  />
                  <Stack direction="horizontal" className="justify-end gap-2">
                    <Button
                      label="Cancel"
                      size="sm"
                      variant="outline"
                      tone="secondary"
                      onPress={onCollapseCategory}
                      isDisabled={isAdding}
                    />
                    <Button
                      label="Add"
                      size="sm"
                      tone="primary"
                      onPress={onSubmitLineItem}
                      isDisabled={isAdding || newItemText.trim() === ""}
                    />
                  </Stack>
                </Stack>
              </Box>
            ) : (
              <Button
                data-testid="add-line-item"
                testID="add-line-item"
                label="Add line item"
                size="sm"
                variant="link"
                tone="primary"
                icon={Plus}
                onPress={expandHandlers[category.name]}
                className="self-start"
              />
            )}
          </Stack>
        );
      })}
    </Stack>

    <AlertDialog
      isOpen={deletingItem !== null}
      onClose={onCancelDelete}
      closeOnOverlayClick={!isDeletingMutation}
      isKeyboardDismissable={!isDeletingMutation}
      useRNModal={Platform.OS !== "web"}
      size="sm"
    >
      <AlertDialogBackdrop className="bg-black" />
      <AlertDialogContent
        testID="delete-line-item-dialog"
        className="border-0 bg-surface-base p-0 dark:bg-prompt-background"
      >
        <AlertDialogBody className="p-5 pb-4">
          <Heading
            variant="title"
            className="shrink text-center text-content-primary dark:text-white"
          >
            Delete this item?
          </Heading>
          <Text
            variant="title-sm-medium"
            className="shrink text-center text-content-muted"
          >
            This line item will be permanently removed from the extracted data.
            This action cannot be undone.
          </Text>
        </AlertDialogBody>
        <AlertDialogFooter className="gap-0 border-t border-outline-default bg-surface-base dark:border-prompt-border dark:bg-prompt-background">
          <Button
            testID="delete-line-item-cancel"
            label="Cancel"
            tone="secondary"
            variant="outline"
            onPress={onCancelDelete}
            isDisabled={isDeletingMutation}
            className="web:hover:!bg-surface-muted dark:web:hover:!bg-white/10 h-14 flex-1 rounded-none border-r border-outline-default !bg-transparent active:!bg-surface-strong dark:border-prompt-border dark:active:!bg-white/15"
          />
          <Button
            testID="delete-line-item-confirm"
            label={isDeletingMutation ? "Deleting..." : "Delete"}
            tone="danger"
            variant="outline"
            onPress={onConfirmDelete}
            isDisabled={isDeletingMutation}
            isLoading={isDeletingMutation}
            className="web:hover:!bg-surface-muted dark:web:hover:!bg-white/10 h-14 flex-1 rounded-none !bg-transparent active:!bg-surface-strong dark:active:!bg-white/15"
          />
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </>
);

ExtractionDataSectionView.displayName = "ExtractionDataSectionView";

export default memo(ExtractionDataSectionView);
